import { SalesStage } from 'models/sales_stages'
import { colors } from 'styles/tokens'

/** Indigo gradient palette ordered from lightest to darkest, mapped by stage position. */
const INDIGO_GRADIENT = [
  '#E8EAF6', // Indigo 50
  '#C5CAE9', // Indigo 100
  '#9FA8DA', // Indigo 200
  '#8692D0', // Indigo 300
  '#5C6BC0', // Indigo 400
  '#3F51B5', // Indigo 500
  '#3949AB', // Indigo 600
  '#303F9F', // Indigo 700
  '#283593', // Indigo 800
  '#1A237E', // Indigo 900
]

/** Special stage colors keyed by lowercase stage name. */
const SPECIAL_STAGE_COLORS: Record<string, { bg: string; text: string }> = {
  'closed won': { bg: '#B9F6CA', text: colors['fg'] },
  'closed lost': { bg: '#FFCDD2', text: colors['fg'] },
  churn: { bg: '#78909C', text: colors['fg-inverse'] },
}

/** Terminal stage names that always appear at the end of the pipeline. */
const TERMINAL_STAGE_NAMES = new Set(['closed won', 'closed lost', 'churn'])

/** Indigo stages with index >= 3 use white text for readability. */
const DARK_TEXT_THRESHOLD = 3

/** Gray fill for inactive/placeholder stage chevrons. */
export const INACTIVE_STAGE_COLOR = colors['surface-disabled']

/** Check if a stage is a terminal stage (Closed Won, Closed Lost, Churn). */
export function isTerminalStage(stage: SalesStage): boolean {
  return TERMINAL_STAGE_NAMES.has(stage.stage.toLowerCase())
}

/**
 * Get the fill color for a stage based on its position in the sorted list.
 * Special stages (Closed Won, Closed Lost, Churn) have fixed colors;
 * regular stages are mapped proportionally across the indigo gradient.
 */
export function getStageColor(
  stage: SalesStage,
  index: number,
  total: number
): { bg: string; text: string } {
  const special = SPECIAL_STAGE_COLORS[stage.stage.toLowerCase()]
  if (special) return special

  const gradientIndex =
    total <= 1
      ? 0
      : Math.round((index / (total - 1)) * (INDIGO_GRADIENT.length - 1))
  const bg = INDIGO_GRADIENT[gradientIndex]
  const text =
    gradientIndex >= DARK_TEXT_THRESHOLD ? colors['fg-inverse'] : colors['fg']
  return { bg, text }
}
